import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardHeader } from '../components/ui/card';
import { CheckCircle2, Wrench, TestTube, Lightbulb, FileText, Upload, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { harnessTranslations } from '../translations/harness';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Bitrix24Modal from '../components/Bitrix24Modal';
import GalleryLightbox from '../components/GalleryLightbox';
import CertificationBadges from '../components/CertificationBadges';

/**
 * HarnessPage Component
 * 
 * Landing page for Harness Tooling services.
 * Showcases assembly solutions, testing capabilities, and engineering support.
 * 
 * Sections:
 * - Hero: Main value proposition for wire harness industry
 * - Assembly Solutions: Fixtures and holders
 * - Testing Solutions: Electrical testing and quality control
 * - Mid CTA: Custom fixture request
 * - Gallery: Examples of fixtures fabricated
 * - Engineering Support: NPI and design assistance
 * - Advantages: Competitive differentiators
 * - Final CTA: Contact information
 * - Footer: Company info and locations
 */
const HarnessPage = () => {
  const { language } = useLanguage();
  const t = harnessTranslations[language];
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="bg-black min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/harness/hero.jpg"
            alt="Custom wire harness assembly fixtures and electrical test benches - DSI Durango Mexico"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/80 to-black/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {t.hero.title} <span className="text-[#B91C1C]">{t.hero.titleAccent}</span>
          </h1>
          <p className="text-xl sm:text-2xl text-gray-300 mb-8 leading-relaxed">
            {t.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => document.getElementById('cta').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#B91C1C]/40"
            >
              <FileText className="mr-2" size={22} />
              {t.hero.cta1}
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Signals - Certification Badges */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <CertificationBadges badges={['iso', 'dynalab']} />
          </div>
        </div>
      </section>

      {/* Assembly Solutions */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.assembly.title} <span className="text-[#B91C1C]">{t.assembly.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.assembly.items.map((item, index) => (
              <Card 
                key={index}
                className="bg-gray-800/50 border-gray-700  backdrop-blur-sm"
              >
                <CardHeader>
                  <div className="flex items-start space-x-4">
                    <Wrench className="text-[#B91C1C] flex-shrink-0 mt-1" size={24} />
                    <p className="text-gray-300 text-base leading-relaxed ">
                      {item}
                    </p>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testing Solutions */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.testing.title} <span className="text-[#B91C1C]">{t.testing.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.testing.items.map((item, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 bg-gray-800/50 border border-gray-700 rounded-lg p-6  backdrop-blur-sm"
              >
                <TestTube className="text-[#B91C1C] flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-300 text-base leading-relaxed ">
                  {item}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-orange-900/20 border border-orange-700 rounded-lg">
            <p className="text-orange-300 text-sm">
              <strong className="font-semibold">{t.testing.note}</strong> {t.testing.noteText}
            </p>
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            <span className="text-[#B91C1C]">{t.midCta.title}</span>
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => document.getElementById('cta').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#B91C1C]/40"
            >
              {t.midCta.button1}
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.gallery.title} <span className="text-[#B91C1C]">{t.gallery.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <GalleryLightbox 
            images={[
              {
                src: '/images/harness/gallery1.jpg',
                alt: 'Custom electrical test fixture for wire harness assembly with holders and test points',
                title: t.gallery.images[0].title,
                caption: t.gallery.images[0].caption
              },
              {
                src: '/images/harness/gallery2.jpg',
                alt: 'Harness assembly fixture with continuity checker and electrical test bench',
                title: t.gallery.images[1].title,
                caption: t.gallery.images[1].caption
              },
              {
                src: '/images/harness/gallery3.jpg',
                alt: 'Industrial test board and assembly fixture for automotive wire harness production',
                title: t.gallery.images[2].title,
                caption: t.gallery.images[2].caption
              }
            ]}
            onQuoteClick={() => setIsModalOpen(true)}
            whatsappMessage={language === 'en' 
              ? 'Hello, I would like to request a quote for custom harness fixtures similar to the examples I saw in your gallery.'
              : 'Hola, me gustaría solicitar una cotización para fixtures personalizados de arneses similar a los ejemplos que vi en su galería.'
            }
          />
        </div>
      </section>

      {/* Engineering Support */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.engineering.title} <span className="text-[#B91C1C]">{t.engineering.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.engineering.items.map((item, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 bg-gray-800/50 border border-gray-700 rounded-lg p-6  backdrop-blur-sm"
              >
                <Lightbulb className="text-[#B91C1C] flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-300 text-base leading-relaxed ">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.advantages.title} <span className="text-[#B91C1C]">{t.advantages.titleAccent}</span>
            </h2>
            <div className="w-24 h-1 bg-[#B91C1C] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.advantages.items.map((item, index) => (
              <Card 
                key={index}
                className="bg-gray-800/50 border-gray-700  backdrop-blur-sm"
              >
                <CardHeader>
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#B91C1C] flex-shrink-0 mt-1" size={20} />
                    <p className="text-gray-300 text-sm leading-relaxed ">
                      {item}
                    </p>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="cta" className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-10">
            {t.finalCta.title} <span className="text-[#B91C1C]">{t.finalCta.titleAccent}</span>
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => setIsModalOpen(true)}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#B91C1C]/40"
            >
              <FileText className="mr-2" size={22} />
              {t.finalCta.button1}
            </Button>
            <Button 
              size="lg"
              onClick={() => window.open('https://wa.me/526181719959?text=Hola,%20necesito%20información%20sobre%20fixtures%20y%20dispositivos%20para%20arneses', '_blank')}
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-10 py-7 text-lg font-semibold  shadow-xl shadow-[#25D366]/40"
            >
              <MessageCircle className="mr-2" size={22} />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Footer with Contact Section */}
      <Footer />

      {/* Bitrix24 Modal */}
      <Bitrix24Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default HarnessPage;
